import React, { useState, useEffect } from "react";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Link,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Facebook,
  Instagram,
  CheckCircle2,
  ExternalLink,
  X as LucideX,
  Copy,
  AlertTriangle,
  Info,
} from "lucide-react";

import api from "../../services/api";
import { REQUIRE_BUSINESS_MANAGEMENT } from "../../config/env";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    dialogPaper: {
      ...(ds.dialogPaper || {}),
      background: isDark ? "#121212" : "#ffffff",
      maxWidth: 640,
    },
    header: {
      ...(ds.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(ds.headerTitle || {}),
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    headerBrandIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      borderRadius: 8,
      background: "rgba(255,255,255,0.15)",
    },
    closeButton: {
      backgroundColor: ds.closeButton?.backgroundColor || "rgba(244, 67, 54, 0.1)",
      color: ds.closeButton?.color || "#f44336",
      transition: ds.closeButton?.transition || "all 0.2s ease",
    },
    content: {
      padding: "20px 24px",
      ...theme.scrollbarStyles,
    },
    intro: {
      fontSize: 13.5,
      lineHeight: 1.55,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginBottom: 16,
    },
    stepper: {
      background: "transparent",
      padding: 0,
      "& .MuiStepLabel-label": {
        fontSize: 14,
        fontWeight: 600,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
      "& .MuiStepLabel-iconContainer .MuiStepIcon-root": {
        color: isDark ? "#3a4150" : "#d5d0cb",
      },
      "& .MuiStepLabel-iconContainer .MuiStepIcon-active": {
        color: theme.palette.primary.main,
      },
      "& .MuiStepConnector-line": {
        borderColor: dsColors.borderLight || (isDark ? "#1a1f29" : "#e8e8e8"),
      },
    },
    stepBody: {
      fontSize: 13,
      lineHeight: 1.55,
      color: dsColors.textSecondary || (isDark ? "#cfd8dc" : "#37474f"),
      paddingBottom: 8,
    },
    inlineLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      marginTop: 6,
      color: theme.palette.primary.main,
      fontSize: 13,
      fontWeight: 500,
    },
    urlLabel: {
      fontSize: 11.5,
      fontWeight: 500,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginTop: 10,
      marginBottom: 4,
      display: "block",
    },
    urlBox: {
      display: "flex",
      alignItems: "center",
      background: isDark ? "rgba(255,255,255,0.04)" : "#f5f5f5",
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      borderRadius: 8,
      padding: "6px 10px",
      gap: 8,
      marginBottom: 6,
    },
    urlText: {
      flex: 1,
      fontFamily: "monospace",
      fontSize: 12.5,
      wordBreak: "break-all",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    copyButton: {
      minWidth: "auto",
      padding: "4px 12px",
      fontSize: 12,
      textTransform: "none",
      borderRadius: 8,
    },
    infoBox: {
      borderRadius: 10,
      padding: "12px 14px",
      marginTop: 16,
      marginBottom: 8,
      background: isDark ? "rgba(33,150,243,0.08)" : "rgba(33,150,243,0.06)",
      border: `1px solid ${isDark ? "rgba(33,150,243,0.25)" : "rgba(33,150,243,0.20)"}`,
    },
    infoTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#90caf9" : "#1565c0",
      marginBottom: 6,
    },
    permissionList: {
      margin: "4px 0 4px 18px",
      padding: 0,
      "& li": {
        fontSize: 12.5,
        lineHeight: 1.5,
        color: dsColors.textSecondary || (isDark ? "#cfd8dc" : "#37474f"),
        marginBottom: 2,
      },
    },
    warningBox: {
      borderRadius: 10,
      padding: "12px 14px",
      marginTop: 12,
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      background: isDark ? "rgba(255,152,0,0.10)" : "rgba(255,152,0,0.08)",
      border: `1px solid ${isDark ? "rgba(255,152,0,0.30)" : "rgba(255,152,0,0.25)"}`,
      color: isDark ? "#ffcc80" : "#a06600",
    },
    warningText: {
      fontSize: 12.5,
      lineHeight: 1.5,
      "& strong": {
        color: isDark ? "#ffd54f" : "#7a4900",
      },
    },
    errorBox: {
      borderRadius: 10,
      padding: "12px 14px",
      marginTop: 12,
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      background: isDark ? "rgba(244,67,54,0.10)" : "rgba(244,67,54,0.06)",
      border: `1px solid ${isDark ? "rgba(244,67,54,0.30)" : "rgba(244,67,54,0.20)"}`,
      color: isDark ? "#ef9a9a" : "#c62828",
    },
    errorText: {
      fontSize: 12.5,
      lineHeight: 1.5,
      "& strong": {
        color: isDark ? "#ef9a9a" : "#7f1d1d",
      },
    },
    actions: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8,
      padding: "12px 16px",
      flexShrink: 0,
    },
    cancelBtn: {
      borderRadius: 12,
      textTransform: "none",
      fontSize: 13,
      fontWeight: 500,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    connectButton: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 600,
      fontSize: 14,
      padding: "10px 24px",
    },
    facebookButton: {
      background: "#1877F2",
      color: "#fff",
      "&:hover": {
        background: "#0e5fc7",
      },
    },
    instagramButton: {
      background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
      color: "#fff",
      "&:hover": {
        filter: "brightness(1.1)",
      },
    },
  };
});

const FacebookModal = ({ open, onClose, channel = "facebook" }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const [dbAppId, setDbAppId] = useState("");

  const FACEBOOK_APP_ID = dbAppId;

  useEffect(() => {
    if (open) {
      api.get("/integrations/meta/credentials")
        .then(({ data }) => {
          setDbAppId(data?.social?.facebookAppId || "");
        })
        .catch(() => {});
    }
  }, [open]);

  const baseUrl = window.location.origin;
  const privacyUrl = `${baseUrl}/politicas`;
  const termsUrl = `${baseUrl}/termos`;
  const dataDeletionUrl = `${baseUrl}/exclusao-dados`;

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 2000);
  };

  const isFacebook = channel === "facebook";
  const channelName = isFacebook ? "Facebook Messenger" : "Instagram Direct";

  const scopes = REQUIRE_BUSINESS_MANAGEMENT
    ? "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,instagram_basic,instagram_manage_messages,business_management"
    : "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,instagram_basic,instagram_manage_messages";

  const handleResponse = (response) => {
    if (!response || response.status === "unknown") {
      setLoading(false);
      return;
    }

    const accessToken =
      response?.accessToken || response?.authResponse?.accessToken;

    const userId =
      response?.id || response?.userID || response?.authResponse?.userID;

    if (!userId || !accessToken) {
      toast.error("Erro ao obter dados do Facebook. Tente novamente.");
      setLoading(false);
      return;
    }

    setLoading(true);

    const payload = {
      facebookUserId: userId,
      facebookUserToken: accessToken,
    };

    if (!isFacebook) {
      payload.addInstagram = true;
    }

    api
      .post("/facebook", payload)
      .then(() => {
        toast.success(
          isFacebook
            ? i18n.t("facebookModal.success.facebook")
            : i18n.t("facebookModal.success.instagram")
        );
        onClose();
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderUrlRow = (label, url, fieldKey) => (
    <>
      <span className={classes.urlLabel}>{label}</span>
      <Box className={classes.urlBox}>
        <Typography component="span" className={classes.urlText}>
          {url}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          className={classes.copyButton}
          onClick={() => handleCopy(url, fieldKey)}
          startIcon={
            copiedField === fieldKey ? (
              <CheckCircle2 size={14} strokeWidth={2.2} color="#4caf50" />
            ) : (
              <Copy size={14} strokeWidth={2} />
            )
          }
        >
          {copiedField === fieldKey
            ? i18n.t("facebookModal.steps.step1c.copied") || "Copiado"
            : i18n.t("facebookModal.buttons.copy") || "Copiar"}
        </Button>
      </Box>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      classes={{ paper: classes.dialogPaper }}
      fullWidth
      maxWidth="md"
    >
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          <span className={classes.headerBrandIcon}>
            {isFacebook ? (
              <Facebook size={16} strokeWidth={2.2} color="#fff" />
            ) : (
              <Instagram size={16} strokeWidth={2.2} color="#fff" />
            )}
          </span>
          {i18n.t("facebookModal.title", { channel: channelName })}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)";
            e.currentTarget.style.color = "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
            e.currentTarget.style.color = "#ffffff";
          }}
        >
          <LucideX size={18} strokeWidth={2.5} />
        </IconButton>
      </div>

      <DialogContent dividers className={classes.content}>
        <Typography className={classes.intro}>
          {i18n.t("facebookModal.description", { channel: channelName })}
        </Typography>

        <Stepper orientation="vertical" activeStep={-1} className={classes.stepper}>
          <Step active expanded>
            <StepLabel>{i18n.t("facebookModal.steps.step1.title")}</StepLabel>
            <StepContent className={classes.stepBody}>
              <div>{i18n.t("facebookModal.steps.step1.description")}</div>
              <Link
                href="https://developers.facebook.com/apps/"
                target="_blank"
                rel="noopener noreferrer"
                className={classes.inlineLink}
              >
                developers.facebook.com
                <ExternalLink size={13} strokeWidth={2} />
              </Link>
            </StepContent>
          </Step>

          <Step active expanded>
            <StepLabel>{i18n.t("facebookModal.steps.step1b.title")}</StepLabel>
            <StepContent className={classes.stepBody}>
              {i18n.t("facebookModal.steps.step1b.description")}
            </StepContent>
          </Step>

          <Step active expanded>
            <StepLabel>{i18n.t("facebookModal.steps.step1c.title")}</StepLabel>
            <StepContent className={classes.stepBody}>
              <div>{i18n.t("facebookModal.steps.step1c.description")}</div>
              {renderUrlRow(
                i18n.t("facebookModal.steps.step1c.privacyUrl"),
                privacyUrl,
                "privacy"
              )}
              {renderUrlRow(
                i18n.t("facebookModal.steps.step1c.termsUrl"),
                termsUrl,
                "terms"
              )}
              {renderUrlRow(
                i18n.t("facebookModal.steps.step1c.dataDeletionUrl") ||
                  "URL de Exclusão de Dados (Data Deletion):",
                dataDeletionUrl,
                "deletion"
              )}
            </StepContent>
          </Step>

          <Step active expanded>
            <StepLabel>{i18n.t("facebookModal.steps.step2.title")}</StepLabel>
            <StepContent className={classes.stepBody}>
              {i18n.t("facebookModal.steps.step2.description")}
            </StepContent>
          </Step>

          {!isFacebook && (
            <Step active expanded>
              <StepLabel>
                {i18n.t("facebookModal.steps.step3instagram.title")}
              </StepLabel>
              <StepContent className={classes.stepBody}>
                {i18n.t("facebookModal.steps.step3instagram.description")}
              </StepContent>
            </Step>
          )}

          <Step active expanded>
            <StepLabel>
              {i18n.t("facebookModal.steps.stepConnect.title")}
            </StepLabel>
            <StepContent className={classes.stepBody}>
              {i18n.t("facebookModal.steps.stepConnect.description", {
                channel: channelName,
              })}
            </StepContent>
          </Step>
        </Stepper>

        <Box className={classes.infoBox}>
          <div className={classes.infoTitle}>
            <Info size={15} strokeWidth={2.2} />
            {i18n.t("facebookModal.permissions.title")}
          </div>
          <ul className={classes.permissionList}>
            <li>{i18n.t("facebookModal.permissions.sendReceive")}</li>
            <li>{i18n.t("facebookModal.permissions.pagesList")}</li>
            <li>{i18n.t("facebookModal.permissions.managePages")}</li>
            {!isFacebook && (
              <li>{i18n.t("facebookModal.permissions.instagramMessages")}</li>
            )}
          </ul>
        </Box>

        <Box className={classes.warningBox}>
          <AlertTriangle size={15} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
          <Typography component="span" className={classes.warningText}>
            <strong>{i18n.t("facebookModal.warning.title")}</strong>{" "}
            {isFacebook
              ? i18n.t("facebookModal.warning.facebook")
              : i18n.t("facebookModal.warning.instagram")}
          </Typography>
        </Box>

        {!FACEBOOK_APP_ID && (
          <Box className={classes.errorBox}>
            <AlertTriangle size={15} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
            <Typography component="span" className={classes.errorText}>
              <strong>{i18n.t("facebookModal.errors.noAppIdTitle")}</strong>{" "}
              {i18n.t("facebookModal.errors.noAppId")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <div className={classes.actions}>
        <Button onClick={onClose} className={classes.cancelBtn}>
          {i18n.t("facebookModal.buttons.cancel")}
        </Button>

        {FACEBOOK_APP_ID ? (
          <FacebookLogin
            appId={FACEBOOK_APP_ID}
            autoLoad={false}
            fields="name,email,picture"
            version="18.0"
            scope={scopes}
            authType="rerequest"
            returnScopes={true}
            disableMobileRedirect={true}
            callback={handleResponse}
            render={(renderProps) => (
              <Button
                variant="contained"
                className={`${classes.connectButton} ${
                  isFacebook ? classes.facebookButton : classes.instagramButton
                }`}
                onClick={() => {
                  setLoading(true);
                  renderProps.onClick();
                }}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : isFacebook ? (
                    <Facebook size={16} strokeWidth={2.2} />
                  ) : (
                    <Instagram size={16} strokeWidth={2.2} />
                  )
                }
              >
                {loading
                  ? i18n.t("facebookModal.buttons.connecting")
                  : i18n.t("facebookModal.buttons.connect", { channel: channelName })}
              </Button>
            )}
          />
        ) : (
          <Button
            variant="contained"
            className={`${classes.connectButton} ${
              isFacebook ? classes.facebookButton : classes.instagramButton
            }`}
            disabled
            startIcon={
              isFacebook ? (
                <Facebook size={16} strokeWidth={2.2} />
              ) : (
                <Instagram size={16} strokeWidth={2.2} />
              )
            }
          >
            {i18n.t("facebookModal.buttons.connect", { channel: channelName })}
          </Button>
        )}
      </div>
    </Dialog>
  );
};

export default FacebookModal;
